// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceTree.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  // TODO: This list should be collected from API and it varies per database
  // server types.
  var SourceTypes = {
    TABLE: 'Table',
    VIEW: 'View',
    PROCEDURE: 'Stored Procedure',
    FUNCTION: 'Function'
  };
  const {
    has,
    isEmpty,
    map,
    partition,
    uniqueId,
    escapeRegExp,
    concat,
    invoke,
    matchesProperty,
    filter,
    values,
    placeholder
  } = require('lodash/fp');

  /**
   * Check whether query source has name that matches the filter string (non-case
   * sensitive)
   * @param  {String}  filter
   * @return {Boolean} Matched or not
   */
  function isMatchedSource(filterItem) {
    return function(item) {
      var filterRegex = new RegExp(escapeRegExp(filterItem), 'i');
      return !has('type', item) || filterRegex.test(item.name);
    };
  }

  /**
   * Recursively create child nodes of a query source node. It allows to auto-group
   * child nodes by its type.
   * @param  {String} filter                        Name-matching filter string
   * @param  {Boolean} autoGroupByType              Group by query source type
   * @param  {Function} querySourceNodeTransformer  Query source node factory
   * @param  {Array} querySources                   Query source array
   * @return {Array}                                Query source node array
   */
  function createChildNodes(filterItem, autoGroupByType, querySourceNodeTransformer, querySources) {
    var match = isMatchedSource(filterItem);
    var selectSatisfiedSources = filter(match, querySources);
    var childNodes = [];
    var processingSources = selectSatisfiedSources;
    if (autoGroupByType) {
      // Divide query sources into type and non-type category
      // It returns two 2 arrays according to type and non-type category.
      var groups = partition(has('type'), selectSatisfiedSources);
      var typeGroup = groups[0];
      var nonTypeGroup = groups[1];

      if (!isEmpty(typeGroup)) {
        processingSources = nonTypeGroup;

        childNodes = values(SourceTypes)
          .map(function(type) {
            return {
              id: uniqueId(type),
              name: type,
              group: true,
              querySources: filter(function(item) {
                return item.type === type;
              }, typeGroup)
            };
          })
          .map(querySourceNodeTransformer(filterItem, false));
      }
    }

    var rest = map(querySourceNodeTransformer(filterItem, autoGroupByType), processingSources);

    return concat(childNodes, rest);
  }

  /**
   * Reactive query source -> node transformer
   * @param  {Object} querySource
   * @return {QuerySourceNode}
   */
  var querySourceNodeTransformer = function(filterItem, autoGroupByType) {
    return function(querySource) {
      return new QuerySourceNode(filterItem, autoGroupByType, querySource);
    };
    // return m.createTransformer(function (querySource) {
    //    return new QuerySourceNode(filter, autoGroupByType, querySource);
    // });
  };

  /**
   * It is a model of a node in query source tree. It carries the query source
   * instance and relevant UI info.
   * @param {[type]} filter          [description]
   * @param {[type]} autoGroupByType [description]
   * @param {[type]} item            [description]
   */
  function QuerySourceNode(filterItem, autoGroupByType, item) {
    this.dataSource = item;
    this.id = item.id;
    this.name = item.name;
    var props = {
      change: item.physicalChange,
      approval: item.approval,
      selected: item.selected,
      childNodes: function() {
        return isEmpty(item.querySources)
          ? []
          : createChildNodes(filterItem, autoGroupByType, querySourceNodeTransformer, item.querySources);
      }
    };

    m.extendObservable(this, props);
  }

  /**
   * Toggle selection of current node and its child nodes regarding to
   * the selecting status.
   * @param  {bool} showSelected Only toggle selection of selecting status.
   */
  QuerySourceNode.prototype.toggleSelect = m.action('toggleSelectQuerySource', function(showSelected) {
    var dataSource = this.dataSource;
    if (dataSource.group && this.childNodes) {
      return placeholder(this.childNodes)
        .filter(matchesProperty('dataSource.selected', showSelected))
        .map(invoke('toggleSelect'))
        .some(Boolean);
    } else if (!dataSource.disabled && has('selected', dataSource)) {
      dataSource.selected = !dataSource.selected;
      return true;
    }
    return false;
  });

  /**
   * Count child nodes that satisfies passing predication.
   * @param  {Function} predicate
   */
  QuerySourceNode.prototype.childCount = function(predicate) {
    return placeholder(this.childNodes)
      .filter(predicate)
      .value().length;
  };

  /**
   * Is typed group node of query source (ie. tables, functions, SPs...)
   * @return {Boolean}
   */
  QuerySourceNode.prototype.isTypeGroup = function() {
    return !!this.dataSource.group;
  };

  /**
   * Tree model of query source. It starts by default root node (id:#root).
   * @param {Array} querySources Query source array
   */
  function QuerySourceTree(querySources) {
    // Only take query sources that satisfies predication
    var props = {
      filter: '',
      root: function() {
        return querySourceNodeTransformer(this.filter, true)({
          id: '#root',
          name: 'root',
          querySources: querySources
        });
      }
    };

    m.extendObservable(this, props);
  }

  /**
   * Set name-matching filter string for query source tree.
   * Whenever it set, all decendant node's name must satisfy filter string.
   * @param {String} newVal New filter string value.
   */
  QuerySourceTree.prototype.setFilter = function(newVal) {
    this.filter = newVal;
  };

  module.exports = QuerySourceTree;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.QuerySourceTree.js